import React, { useEffect, useState, type FC } from 'react';
import classnames from 'classnames';
import {
  PollQuestionType,
  PollSelectionType,
  type IPollQuestion,
  type IPollChoice,
  toPollQuestion,
} from './records';
import ReactiveContainer from 'components/common/ReactiveContainer';
import { useInputDebounceCallback } from 'components/hooks/useInputDebounceCallback';
import { getAnswer, postAnswer } from './api';
import MultipleChoice from './MultipleChoice';
import StarRating from './StarRating';
import NumberScale from './NumberScale';
import CustomerSatisfaction from './CustomerSatisfaction';
import styles from './Poll.module.css';

interface PollProps {
  question?: any;
  jwt?: any;
  moderator?: any;
  link_to_session?: any;
  show?: 'open' | 'results';
  read_only?: boolean;
  saved_feedback_text?: string;
  done_label?: string;
  close_label?: string;
  is_in_portal?: boolean;
}

function reactiveUrl(questionId: string, link: boolean): string {
  const query = link ? `?session_link=true` : '';
  return `/polls/questions/${questionId}/results.reactive${query}`;
}

function getPostIndexUrl(): string {
  return window.location.href.split('?poll_id=')[0];
}

const DEBOUNCE_TIMEOUT = 500;

const Poll: FC<PollProps> = ({
  question,
  jwt,
  moderator,
  link_to_session,
  show = 'open',
  read_only = false,
  saved_feedback_text = 'Saved!',
  done_label = 'Done',
  close_label = 'Close',
  is_in_portal = false,
}) => {
  const pollQuestion: IPollQuestion = toPollQuestion(question);
  const showChoices = !read_only && show !== 'results';
  const selectableChoices = show === 'open';
  const singleSelection =
    pollQuestion.selection_type === PollSelectionType.Single;
  const [isSaving, setIsSaving] = useState<boolean | null>(null);

  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [tellUsMore, setTellUsMore] = useInputDebounceCallback(
    '',
    (value: string) => updateTellUsMore(value),
    DEBOUNCE_TIMEOUT
  );

  useEffect(loadPollAnswer, []);

  function loadPollAnswer() {
    if (!pollQuestion.id || !showChoices) return;

    getAnswer(pollQuestion.id, jwt)
      .then(response => {
        const choices = response.choice_ids || [response.choice_id];
        setSelectedChoices(choices);
        setTellUsMore(response.tell_us_more || '');
      })
      .catch(error => console.error(error));
  }

  function selectChoice(choice: IPollChoice) {
    if (!pollQuestion.id || !selectableChoices) return;
    // If the question is single selection and the choice is already selected, do nothing
    if (singleSelection && selectedChoices.includes(choice.id)) return;

    setIsSaving(true);

    // Optimistically update the selected choices
    setSelectedChoices(prevChoices => {
      if (singleSelection) {
        return [choice.id];
      } else {
        // For multiple choice, toggle the selection
        return prevChoices.includes(choice.id)
          ? prevChoices.filter(id => id !== choice.id)
          : [...prevChoices, choice.id];
      }
    });

    postAnswer({
      questionId: pollQuestion.id,
      jwt: jwt,
      choiceId: choice.id,
      tellUsMore: tellUsMore,
    })
      .then(response => {
        if (response.choice_ids) {
          setSelectedChoices(response.choice_ids);
        } else if (response.choice_id !== undefined) {
          // Legacy API format
          setSelectedChoices([response.choice_id]);
        }
        setTellUsMore(response.tell_us_more || '');
      })
      .catch(error => console.log(error))
      .finally(() => setIsSaving(false));
  }

  function updateTellUsMore(value: string) {
    if (!pollQuestion.id || !selectableChoices) return;

    setIsSaving(true);

    postAnswer({
      questionId: pollQuestion.id,
      jwt: jwt,
      tellUsMore: value,
    })
      .catch(error => console.error(error))
      .finally(() => setIsSaving(false));
  }

  return (
    <>
      <div className={styles.Poll} id={`poll-${pollQuestion.id}`}>
        <div className={styles.header}>
          <i
            className={classnames(
              'fa-regular fa-square-poll-vertical',
              styles.headerIcon
            )}
          />
          <h3>{pollQuestion.name}</h3>
        </div>
        {showChoices &&
          pollQuestion.question_type === PollQuestionType.MultipleChoice && (
            <MultipleChoice
              question={pollQuestion}
              selectedChoiceIds={selectedChoices}
              onSelectChoice={selectChoice}
            />
          )}
        {showChoices &&
          pollQuestion.question_type === PollQuestionType.StarRating && (
            <StarRating
              question={pollQuestion}
              selectedChoiceId={selectedChoices[0]}
              onSelectChoice={selectChoice}
            />
          )}
        {showChoices &&
          pollQuestion.question_type === PollQuestionType.NumberScale && (
            <NumberScale
              question={pollQuestion}
              selectedChoiceId={selectedChoices[0]}
              onSelectChoice={selectChoice}
            />
          )}
        {showChoices &&
          pollQuestion.question_type ===
            PollQuestionType.CustomerSatisfaction && (
            <CustomerSatisfaction
              question={pollQuestion}
              selectedChoiceId={selectedChoices[0]}
              onSelectChoice={selectChoice}
            />
          )}
        {showChoices && pollQuestion.tell_us_more_enabled && (
          <>
            <hr />
            <textarea
              className={styles.tellUsMore}
              placeholder={pollQuestion.tell_us_more_placeholder}
              value={tellUsMore}
              onChange={e => setTellUsMore(e.target.value)}
            />
          </>
        )}
        {moderator && pollQuestion.id && (
          <ReactiveContainer
            reactivePath={`/Polls::Question-${pollQuestion.id}`}
            reactiveUrl={reactiveUrl(pollQuestion.id, link_to_session)}
          />
        )}
      </div>
      <div
        className={`${styles.closeButtonWrapper} ${
          is_in_portal ? styles.closeButtonWrapperInPortal : ''
        }`}
      >
        <span className={`${styles.selectedChoiceNotice}`}>
          {!isSaving && !!selectedChoices.length && saved_feedback_text}
          {isSaving && <aha-spinner />}
        </span>
        {show === 'open' && is_in_portal && (
          <aha-button kind='primary' href={getPostIndexUrl()}>
            {done_label}
          </aha-button>
        )}
        {show === 'open' && !is_in_portal && (
          <aha-button
            kind='default'
            onClick={() => {
              const pollContainer = document.getElementById(
                `poll-${pollQuestion.id}`
              );
              if (pollContainer) {
                pollContainer.dispatchEvent(
                  new CustomEvent('poll-close', {
                    bubbles: true,
                    composed: true,
                  })
                );
              }
            }}
          >
            {close_label}
          </aha-button>
        )}
      </div>
    </>
  );
};

export default Poll;
